import { CheckCircle2, CreditCard, Package, User, XCircle } from 'lucide-react'

import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Separator } from '@/components/ui/separator'

import { useBusiness } from '@/contexts/BusinessContext'

import { ReturnRequest } from '@/types/returns'

const RETURN_STATUS_TONE: Partial<Record<ReturnRequest['status'], BadgeTone>> = {
  pending: 'warning',
  approved: 'info',
  completed: 'success',
  rejected: 'error',
}

interface ReturnDetailsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  request: ReturnRequest | null
  onApprove: (id: string) => void
  onReject: (id: string) => void
}

export const ReturnDetailsDialog = ({
  open,
  onOpenChange,
  request,
  onApprove,
  onReject,
}: ReturnDetailsDialogProps) => {
  const { formatCurrency } = useBusiness()

  if (!request) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Return {request.id}
            <Badge tone={RETURN_STATUS_TONE[request.status] ?? 'soft'} className="capitalize">
              {request.status}
            </Badge>
          </DialogTitle>
          <DialogDescription>Created on {request.createdAt.toLocaleDateString()}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          {/* Customer & Order Info */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <h4 className="flex items-center gap-2 text-sm text-muted-foreground">
                <User className="size-4" /> Customer
              </h4>
              <p className="text-sm font-medium text-ink">{request.customerName}</p>
              <p className="text-sm text-muted-foreground">{request.customerEmail}</p>
            </div>
            <div className="space-y-1">
              <h4 className="flex items-center gap-2 text-sm text-muted-foreground">
                <Package className="size-4" /> Order
              </h4>
              <p className="text-sm font-medium text-ink">{request.orderId}</p>
              {request.trackingNumber && (
                <p className="text-sm text-muted-foreground">Tracking: {request.trackingNumber}</p>
              )}
            </div>
          </div>

          <Separator />

          {/* Items */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-ink">Returned items</h4>
            <div className="divide-y divide-border-subtle rounded-lg border border-border">
              {request.items.map((item, idx) => (
                <div key={idx} className="flex items-start justify-between gap-4 p-3">
                  <div>
                    <p className="text-sm font-medium text-ink">{item.productName}</p>
                    <div className="mt-1 flex gap-2">
                      <Badge tone="soft">Qty {item.quantity}</Badge>
                      <Badge tone="soft">{item.condition}</Badge>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium tabular-nums text-ink">{formatCurrency(item.price)}</p>
                    <p className="mt-1 text-xs text-muted-foreground">Reason: {item.reason}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Refund Info */}
          <div className="flex flex-col gap-2 rounded-lg bg-subdued p-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 text-sm">
              <CreditCard className="size-4 text-muted-foreground" />
              <span className="text-muted-foreground">Refund method</span>
              <span className="capitalize text-ink">{request.refundMethod.replace('_', ' ')}</span>
            </div>
            <div className="text-sm sm:text-right">
              <span className="mr-2 text-muted-foreground">Total refund</span>
              <span className="font-semibold tabular-nums text-ink">{formatCurrency(request.refundAmount)}</span>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {request.status === 'pending' && (
            <div className="flex w-full gap-2 sm:w-auto">
              <Button
                variant="destructive"
                onClick={() => {
                  onReject(request.id)
                  onOpenChange(false)
                }}
              >
                <XCircle className="size-4" />
                Reject
              </Button>
              <Button
                onClick={() => {
                  onApprove(request.id)
                  onOpenChange(false)
                }}
              >
                <CheckCircle2 className="size-4" />
                Approve
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
